/**
 * Словари листания: показатели сортировки, плитки раздела «Товары», площадки,
 * порядок и фильтры. Часть `lib/productNav` (нарезка 14.10.26) — публичные
 * имена те же.
 */

export const METRIC_LABEL = {
  views: "показы",
  open_card: "заходы",
  cart: "корзина",
  orders: "заказы",
  buys: "выкупы",
  // Чистая прибыль окна — формула витрины карточки (15.09.26, раздел «Товары»).
  profit: "чистая прибыль",
  reorder: "к заказу",
  // Дней запаса — остаток склада ÷ чистый выкуп окна уходимости в день
  // (разметка владельца 24.09.26). Площадка — переключатель тулбара.
  cover: "дней запаса",
} as const;

export type NavMetric = keyof typeof METRIC_LABEL;

export function isNavMetric(value: string | null): value is NavMetric {
  return value !== null && Object.hasOwn(METRIC_LABEL, value);
}

/**
 * Показатели, которые раздел «Товары» умеет показать НА ПЛИТКЕ склейки
 * (замечание владельца 16.09.26: «добавь возможность настройки показа, какие
 * параметры статистики здесь показываются»). Спека —
 * `docs/specs/2026-09-16-products-tile-metrics-design.md`.
 *
 * ПОРЯДОК КЛЮЧЕЙ — ПОРЯДОК СТРОК НА ПЛИТКЕ: воронка сверху вниз, затем выкуп %,
 * прибыль и «к заказу». Набор чекбоксов порядка не несёт, а плитки обязаны
 * читаться одинаково.
 *
 * Список шире сортировочного на `buyout` и короче подписями: «прибыль» вместо
 * «чистая прибыль» — на плитке рядом стоит число, а не список показателей.
 */
export const TILE_LABEL = {
  views: "показы",
  open_card: "заходы",
  cart: "корзина",
  orders: "заказы",
  buys: "выкупы",
  buyout: "выкуп %",
  profit: "прибыль",
  reorder: "к заказу",
  // ЧИСЛА СКЛАДА идут последними: они не про окно статистики, а про то, что
  // лежит сейчас (18.09.26; спека `docs/specs/2026-09-18-shelf-list-filters-design.md`).
  // Площадку называет сама подпись: полки у них разные, и «довести» без имени
  // площадки читалось бы как одно число на обе (23.09.26; спека
  // `docs/specs/2026-09-23-ozon-shelf-design.md`). Ключ `shelf` при этом
  // остаётся прежним — на нём стоят ссылки перехода `?metric=` и память кольца.
  shelf: "довести WB",
  shelf_ozon: "довести Ozon",
  korob: "в коробах",
  wb_stock: "склад WB",
  ozon_stock: "склад Ozon",
  // ОБЕЩАНО ФАБРИКОЙ — подписи итога карточки товара (разметка владельца
  // 25.09.26 14:59 п.1: «отфильтровать по заказам»). Не путать с «заказы»
  // воронки выше: там покупатели, здесь фабрика.
  plan_left: "в планах",
  in_orders: "в заказах",
  // Дни запаса площадок — под фильтр «дней запаса WB до 14» (24.09.26).
  // Площадку называет подпись, как у складов строкой выше.
  cover_wb: "дней запаса WB",
  cover_ozon: "дней запаса Ozon",
  // ЦЕНЫ ПЛОЩАДОК — последними: они не про окно статистики, а про «сколько
  // стоит сейчас», как числа склада выше. Площадку называет сама подпись, и
  // переключателю площадки тулбара они не подчиняются (19.09.26; спека
  // `docs/specs/2026-09-19-products-prices-analytics-design.md`).
  price_wb: "цена WB",
  price_ozon: "цена Ozon",
} as const;

export type TileMetric = keyof typeof TILE_LABEL;

/**
 * Ключ СТРОКИ на плитке и под фото — шире набора чекбоксов ровно на
 * «прибыль/шт» (16.09.26, замечание владельца: «когда я выбираю „показать
 * прибыль“, показывай прибыль в разрезе: общий и на штуку»).
 *
 * Своей отметки у штуки нет, и это решение владельца: сумма и штука — один
 * вопрос в двух масштабах, а девятый чекбокс позволил бы показать штуку без
 * суммы, на которой она построена.
 */
export const PROFIT_UNIT = "profit_unit";

/** Ключи СТРОК СПП. Своей отметки у них нет — СПП едет со своей ценой, как
 *  «прибыль/шт» едет с прибылью: процент без цены, от которой он посчитан,
 *  читается как факт площадки, хотя это наш расчёт по неделе. */
export const SPP_ROW = { price_wb: "spp_wb", price_ozon: "spp_ozon" } as const;

export type TileRowKey = TileMetric | typeof PROFIT_UNIT | "spp_wb" | "spp_ozon" | typeof COVER_METRIC;

/** Подписи СТРОК: набор чекбоксов плюс штука прибыли и строки СПП. Чекбоксы
 *  рисуются по `TILE_LABEL`, поэтому лишнего пункта в шестерёнке не появляется. */
export const ROW_LABEL: Record<TileRowKey, string> = {
  ...TILE_LABEL,
  [PROFIT_UNIT]: "прибыль/шт",
  spp_wb: "СПП WB",
  spp_ozon: "СПП Ozon",
  // Показатель сортировки без отметки плитки: строкой он встаёт только первым.
  cover: METRIC_LABEL.cover,
};

/** Дней запаса: сортировка и два числа плитки. `null` у них — «∞», продаж за
 *  окно нет, а не «данных нет»: остаток известен всегда. */
export const COVER_METRIC = "cover";
export const COVER_KEYS: readonly TileRowKey[] = [COVER_METRIC, "cover_wb", "cover_ozon"];

export function isCoverKey(key: string): boolean {
  return (COVER_KEYS as readonly string[]).includes(key);
}

/** Дорогие показатели: считаются походом в финотчёт либо в CRM, и о цене
 *  предупреждает подпись группы в шестерёнке. */
export const TILE_SLOW: TileMetric[] = [
  "profit",
  "reorder",
  "shelf",
  "shelf_ozon",
  "korob",
  "wb_stock",
  "ozon_stock",
  "plan_left",
  "in_orders",
  "cover_wb",
  "cover_ozon",
];

/** Числа плитки: запрошенные показатели, слагаемые выкупа % (`buys` и
 *  `deliveries`) и штука прибыли — из первых склейка считает процент, из
 *  прибыли с `buys` — свой ₽/шт; ими же подписаны подсказки. */
export type TileValues = Partial<
  Record<
    | TileRowKey
    | "deliveries"
    // «Платит клиент» своей СТРОКИ не имеет — он едет вторым числом в строке
    // цены, — но ключ в ответе свой, и без него `priceRow` его не прочтёт.
    | "pays_wb"
    | "pays_ozon"
    | `${"price" | "pays" | "spp"}_${"wb" | "ozon"}_max`
    // Дни наличия — знаменатель поправки на дефицит отчёта «Аналитика → ABC»
    // (`ranking/service.py::tile_values`, ключ `stock_days`): своей отметки в
    // `SHOW` у них нет, они едут рядом с прибылью тем же приёмом, что и
    // `profit_unit`/`buys` (находка финального ревью 21.09.26 — без ключа тип
    // молчал бы структурным совпадением, а не контрактом).
    | "stock_days"
    // Слагаемые дней запаса: склейка делит сложенный остаток на сложенную
    // скорость (`similar.py::fold_values`), и подсказка ими подписана.
    | `cover_${"wb" | "ozon"}_${"stock" | "speed"}`,
    number | null
  >
>;

export function isTileMetric(value: unknown): value is TileMetric {
  return typeof value === "string" && Object.hasOwn(TILE_LABEL, value);
}

export const PLATFORM_LABEL = {
  both: "обе площадки",
  wb: "WB",
  ozon: "Ozon",
} as const;

export type NavPlatform = keyof typeof PLATFORM_LABEL;

export const DIR_LABEL = {
  desc: "сначала лучшие",
  asc: "сначала худшие",
} as const;

export type NavDir = keyof typeof DIR_LABEL;

/** Подписи порядка под показатель: у дней запаса «лучшие» ничего не значат, и
 *  `asc` здесь — меньше дней сверху (`ranking.order_cover`). */
export function dirLabels(metric: NavMetric): Record<NavDir, string> {
  return metric === COVER_METRIC ? { asc: "сначала меньше дней", desc: "сначала больше дней" } : DIR_LABEL;
}

/**
 * Фильтры набора кольца (замечания владельца 15.09.26 на шестерёнке): есть ли у
 * товара активный план и есть ли потребность. Значения одни на оба фильтра —
 * все · есть · нет; числа те же, что на карточке: итог «в планах» и итог «к
 * заказу» по окну уходимости. Спека — `docs/specs/2026-09-15-nav-plan-filter-design.md`.
 */
export type NavFilter = "all" | "yes" | "no";

export const PLAN_FILTER_LABEL: Record<NavFilter, string> = {
  all: "все",
  yes: "есть план",
  no: "нет плана",
};

export const NEED_FILTER_LABEL: Record<NavFilter, string> = {
  all: "все",
  yes: "есть потребность",
  no: "нет потребности",
};

export function isFilter(value: unknown): value is NavFilter {
  return value === "all" || value === "yes" || value === "no";
}
